import { useState, useEffect } from 'react';
import { motion, useScroll, useMotionValueEvent } from 'motion/react';
import { 
  LayoutDashboard, 
  Receipt, 
  BarChart3, 
  DollarSign,
  TrendingUp,
  Users,
  Settings,
  Database,
  MoreHorizontal
} from 'lucide-react';
import { cn } from '../ui/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { usePermissions } from '../../hooks/usePermissions';

interface MobileNavigationProps {
  currentPath: string;
  onNavigate: (path: string) => void;
}

/**
 * MobileNavigation - Bottom Tab Bar
 * 
 * Padrão da indústria: Auto-hide on scroll
 * - Esconde ao rolar para baixo (mais espaço para conteúdo)
 * - Aparece ao rolar para cima (usuário quer navegar)
 * - Sempre acessível ao tocar no topo da tela
 * 
 * Usado por: Instagram, YouTube, Chrome mobile, Safari iOS
 */
export function MobileNavigation({ currentPath, onNavigate }: MobileNavigationProps) {
  const [isVisible, setIsVisible] = useState(true);
  const { scrollY } = useScroll();
  const { canAccess } = usePermissions();
  
  // Auto-hide on scroll (padrão Instagram/YouTube)
  useMotionValueEvent(scrollY, "change", (latest) => {
    const previous = scrollY.getPrevious() ?? 0;
    const diff = latest - previous;
    
    // Esconde ao rolar para baixo (> 5px), mostra ao rolar para cima
    if (diff > 5 && latest > 100) {
      setIsVisible(false);
    } else if (diff < -5) {
      setIsVisible(true);
    }
    
    // Sempre mostra quando está no topo
    if (latest < 50) {
      setIsVisible(true);
    }
  });
  
  const mainNavItems = [
    { title: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { title: 'Lançamentos', icon: Receipt, path: '/entries' },
    { title: 'Relatórios', icon: BarChart3, path: '/reports' },
  ];

  // All more items with permission requirements
  const allMoreItems = [
    { title: 'Orçamento', icon: DollarSign, path: '/budget' },
    { title: 'Cenários', icon: TrendingUp, path: '/scenarios' },
    { title: 'Compartilhamento', icon: Users, path: '/sharing', requiresAccess: 'sharing' as const },
    { title: 'Dados Mestres', icon: Database, path: '/configurations', requiresAccess: 'configurations' as const },
    { title: 'Configurações', icon: Settings, path: '/settings', requiresAccess: 'settings' as const },
  ];

  // Filter more items based on permissions
  const moreItems = allMoreItems.filter(item => {
    if (item.requiresAccess) {
      return canAccess(item.requiresAccess);
    }
    return true;
  });

  const isMoreItemActive = moreItems.some(item => item.path === currentPath);

  return (
    <motion.nav 
      initial={{ y: 0 }}
      animate={{ y: isVisible ? 0 : 100 }}
      transition={{ 
        duration: 0.3, 
        ease: [0.32, 0.72, 0, 1] // Smooth easing (iOS-like)
      }}
      className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur-lg md:hidden shadow-lg"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom)', // iOS safe area
      }}
    >
      <div className="flex items-center justify-around">
        {mainNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = currentPath === item.path;
          
          return (
            <button
              key={item.path}
              onClick={() => onNavigate(item.path)}
              className={cn(
                "flex flex-1 flex-col items-center gap-1 py-3 px-3 transition-colors min-h-[60px]",
                "active:bg-accent/70 active:scale-95 transition-transform",
                isActive && "text-primary"
              )}
              aria-label={item.title}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon className={cn("h-6 w-6", isActive && "fill-primary/10")} />
              <small className="truncate max-w-full">{item.title}</small>
            </button>
          );
        })}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "flex flex-1 flex-col items-center gap-1 py-3 px-3 transition-colors min-h-[60px]",
                "active:bg-accent/70 active:scale-95 transition-transform",
                isMoreItemActive && "text-primary"
              )}
              aria-label="Mais opções"
            >
              <MoreHorizontal className={cn("h-6 w-6", isMoreItemActive && "fill-primary/10")} />
              <small>Mais</small>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 mr-2 mb-2">
            <DropdownMenuLabel>Planejamento & Gestão</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {moreItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentPath === item.path;
              
              return (
                <DropdownMenuItem
                  key={item.path}
                  onClick={() => onNavigate(item.path)}
                  className={cn(
                    "flex items-center gap-3 cursor-pointer py-3",
                    isActive && "bg-accent"
                  )}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.title}</span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </motion.nav>
  );
}
